import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  IconSearch,
  IconAdjustmentsHorizontal,
  IconBriefcase2,
  IconMapPin,
  IconCalendar,
  IconPlane,
  IconDeviceFloppy,
  IconChevronDown,
  IconX,
  IconStar,
  IconStarFilled,
  IconShare,
  IconShare3,
  IconRotate,
  IconFilter,
  IconCheck,
  IconWorld,
  IconClock,
  IconBriefcase,
  IconSend,
  IconCopy,
  IconCircleCheck,
  IconBrandTwitter,
  IconBrandFacebook,
  IconBrandWhatsapp,
} from "@tabler/icons-react";
import classes from "./searchRedesign.module.css";
import { currencyFormat } from "../../../utils/functions";
import { errorHandler, toastSuccess } from "../../../utils";
import { ALL_SKILLS } from "../../../constants";
import { searchAdvance } from "../../../services/clientCalls";
import {
  getTalentJobFeed,
  talentJobAlert,
  talentJobInteraction,
  talentRemoveJobInteraction,
} from "../../../services/talentCalls";

const EMPLOYMENT_TYPES = ["Full-Time", "Part-Time", "Internship"];
const WORK_MODES = ["Onsite", "Remote", "Relocation"];

const DATE_POSTED_OPTIONS = [
  { value: "", label: "Anytime" },
  { value: "24h", label: "Last 24 hours" },
  { value: "3d", label: "Last 3 days" },
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
  { value: "30d", label: "Last 30 days" },
];

const emptyDrawerFilters = {
  title: "",
  company: "",
  location: "",
  datePosted: "",
  skills: [],
  min: "",
  max: "",
  types: [],
  modes: [],
  relocationOnly: false,
};

const AdvancedSearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [keyword, setKeyword] = useState("");
  const [heroMode, setHeroMode] = useState("");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [quickChips, setQuickChips] = useState({ fulltime: false, remote: false, week: false, relocation: false });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(emptyDrawerFilters);
  const [skillsPanelOpen, setSkillsPanelOpen] = useState(false);

  const [rawJobs, setRawJobs] = useState([]);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [selectedJob, setSelectedJob] = useState(null);
  const [shareJob, setShareJob] = useState(null);
  const [savingSearch, setSavingSearch] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  const runSearch = async (kw = keyword) => {
    setLoading(true);
    try {
      if (kw.trim()) {
        const res = await searchAdvance(kw.trim(), ["title", "description", "skillsAndRequirements"]);
        setRawJobs(res?.data?.data?.data || []);
      } else {
        const res = await getTalentJobFeed(1);
        setRawJobs(res?.data?.data?.records || []);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearched(true);
    setPage(1);
    runSearch();
  };

  // Header search bar lands here with ?query=... — pick it up and actually
  // run the search instead of leaving the box empty. Watches the URL param
  // itself (not just mount) so re-searching from the header while already
  // on this page updates the results too, instead of only working when
  // navigating in fresh from another page.
  useEffect(() => {
    const q = searchParams.get("query");
    if (q && q !== keyword) {
      setKeyword(q);
      setSearched(true);
      setPage(1);
      runSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleQuickChip = (key) => {
    setQuickChips((prev) => ({ ...prev, [key]: !prev[key] }));
    if (!searched) {
      setSearched(true);
      runSearch();
    }
    setPage(1);
  };

  const applyDrawer = () => {
    setDrawerOpen(false);
    setSearched(true);
    setPage(1);
    runSearch();
  };

  const resetDrawer = () => setDrawerFilters(emptyDrawerFilters);

  const toggleDrawerType = (t) =>
    setDrawerFilters((prev) => ({
      ...prev,
      types: prev.types.includes(t) ? prev.types.filter((x) => x !== t) : [...prev.types, t],
    }));

  const toggleDrawerMode = (m) =>
    setDrawerFilters((prev) => ({
      ...prev,
      modes: prev.modes.includes(m) ? prev.modes.filter((x) => x !== m) : [...prev.modes, m],
    }));

  const toggleDrawerSkill = (s) =>
    setDrawerFilters((prev) => ({
      ...prev,
      skills: prev.skills.includes(s) ? prev.skills.filter((x) => x !== s) : [...prev.skills, s],
    }));

  // ── Client-side combined filtering — the API only supports one keyword search
  // across a fixed field list, so quick chips / drawer criteria narrow the
  // fetched result set locally rather than round-tripping per filter. ──
  const filteredJobs = useMemo(() => {
    let list = [...rawJobs];

    if (heroMode) list = list.filter((j) => j.workMode === heroMode);
    if (quickChips.fulltime) list = list.filter((j) => j.type === "Full-Time");
    if (quickChips.remote) list = list.filter((j) => j.workMode === "Remote");
    if (quickChips.week) {
      const weekAgo = moment().subtract(7, "days");
      list = list.filter((j) => j.createdAt && moment(j.createdAt).isAfter(weekAgo));
    }
    if (quickChips.relocation) list = list.filter((j) => j.workMode === "Relocation" || j.hasNoRelocationPlans === "No");

    if (drawerFilters.title.trim()) {
      const t = drawerFilters.title.trim().toLowerCase();
      list = list.filter((j) => j.title?.toLowerCase().includes(t));
    }
    if (drawerFilters.company.trim()) {
      const c = drawerFilters.company.trim().toLowerCase();
      list = list.filter((j) => j.companyName?.toLowerCase().includes(c));
    }
    if (drawerFilters.location.trim()) {
      const l = drawerFilters.location.trim().toLowerCase();
      list = list.filter((j) => j.location?.toLowerCase().includes(l));
    }
    if (drawerFilters.datePosted) {
      const daysMap = { "24h": 1, "3d": 3, "7d": 7, "14d": 14, "30d": 30 };
      const cutoff = moment().subtract(daysMap[drawerFilters.datePosted], "days");
      list = list.filter((j) => j.createdAt && moment(j.createdAt).isAfter(cutoff));
    }
    if (drawerFilters.skills.length) {
      list = list.filter((j) =>
        drawerFilters.skills.some((s) => j.skillsAndRequirements?.some((js) => js.toLowerCase() === s.toLowerCase())),
      );
    }
    if (drawerFilters.min) list = list.filter((j) => Number(j.budget ?? 0) >= Number(drawerFilters.min));
    if (drawerFilters.max) list = list.filter((j) => Number(j.budget ?? 0) <= Number(drawerFilters.max));
    if (drawerFilters.types.length) list = list.filter((j) => drawerFilters.types.includes(j.type));
    if (drawerFilters.modes.length) list = list.filter((j) => drawerFilters.modes.includes(j.workMode));
    if (drawerFilters.relocationOnly) list = list.filter((j) => j.workMode === "Relocation");

    const sorted = list.sort((a, b) => {
      if (sort === "salary-desc") return Number(b.budget ?? 0) - Number(a.budget ?? 0);
      if (sort === "salary-asc") return Number(a.budget ?? 0) - Number(b.budget ?? 0);
      return new Date(b.createdAt || b.startDate || 0) - new Date(a.createdAt || a.startDate || 0);
    });

    return sorted;
  }, [rawJobs, heroMode, quickChips, drawerFilters, sort]);

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const pagedJobs = filteredJobs.slice((page - 1) * pageSize, page * pageSize);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (heroMode) chips.push({ key: "heroMode", label: heroMode, clear: () => setHeroMode("") });
    if (quickChips.fulltime) chips.push({ key: "fulltime", label: "Full-Time", clear: () => toggleQuickChip("fulltime") });
    if (quickChips.remote) chips.push({ key: "remote", label: "Remote", clear: () => toggleQuickChip("remote") });
    if (quickChips.week) chips.push({ key: "week", label: "Posted this week", clear: () => toggleQuickChip("week") });
    if (quickChips.relocation) chips.push({ key: "relocation", label: "Relocation provided", clear: () => toggleQuickChip("relocation") });
    if (drawerFilters.title) chips.push({ key: "title", label: `Title: ${drawerFilters.title}`, clear: () => setDrawerFilters((p) => ({ ...p, title: "" })) });
    if (drawerFilters.company) chips.push({ key: "company", label: `Company: ${drawerFilters.company}`, clear: () => setDrawerFilters((p) => ({ ...p, company: "" })) });
    if (drawerFilters.location) chips.push({ key: "location", label: `Location: ${drawerFilters.location}`, clear: () => setDrawerFilters((p) => ({ ...p, location: "" })) });
    if (drawerFilters.datePosted)
      chips.push({
        key: "datePosted",
        label: DATE_POSTED_OPTIONS.find((o) => o.value === drawerFilters.datePosted)?.label,
        clear: () => setDrawerFilters((p) => ({ ...p, datePosted: "" })),
      });
    drawerFilters.skills.forEach((s) =>
      chips.push({ key: `skill-${s}`, label: s, clear: () => toggleDrawerSkill(s) }),
    );
    if (drawerFilters.min || drawerFilters.max)
      chips.push({
        key: "range",
        label: `₦${drawerFilters.min || 0} – ${drawerFilters.max || "Any"}`,
        clear: () => setDrawerFilters((p) => ({ ...p, min: "", max: "" })),
      });
    drawerFilters.types.forEach((t) => chips.push({ key: `type-${t}`, label: t, clear: () => toggleDrawerType(t) }));
    drawerFilters.modes.forEach((m) => chips.push({ key: `mode-${m}`, label: m, clear: () => toggleDrawerMode(m) }));
    if (drawerFilters.relocationOnly)
      chips.push({ key: "relocOnly", label: "Relocation only", clear: () => setDrawerFilters((p) => ({ ...p, relocationOnly: false })) });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroMode, quickChips, drawerFilters]);

  const handleToggleStar = async (job) => {
    try {
      if (job?.isStarred) {
        await talentRemoveJobInteraction(job._id, "star");
        toastSuccess("Star removed successfully");
      } else {
        await talentJobInteraction({ jobId: job._id, interactionType: "star" });
        toastSuccess("Job starred successfully");
      }
      setRawJobs((prev) => prev.map((j) => (j._id === job._id ? { ...j, isStarred: !j.isStarred } : j)));
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  const handleSaveSearch = async () => {
    setSavingSearch(true);
    try {
      await talentJobAlert({
        role: keyword || drawerFilters.title || undefined,
        skillsAndRequirements: drawerFilters.skills,
      });
      showToast("Search saved");
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setSavingSearch(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [filteredJobs.length]);

  return (
    <div className={classes.main}>
      <div className={classes.pageHeader}>
        <span className={classes.eyebrow}>
          <IconSearch size={14} /> Advanced Search
        </span>
        <h1 className={classes.pageTitle}>Search every open role.</h1>
        <p className={classes.pageSubtitle}>
          One search bar for the whole job feed — refine by work mode, filter deeper, then save the search for later.
        </p>
      </div>

      <div className={classes.hero}>
        <div className={classes.heroInner}>
          <div className={classes.heroEyebrow}>Search jobs</div>
          <h2 className={classes.heroTitle}>Find your next opportunity.</h2>
          <p className={classes.heroSub}>Search by job title, skill, or company, then narrow it down by work mode.</p>
        </div>
        <div className={classes.heroSearchbar}>
          <div className={classes.heroSearchInputWrap}>
            <IconSearch size={17} />
            <input
              type="text"
              placeholder="Job title, skill, or company"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div className={classes.workmodeSeg}>
            {[{ v: "", label: "Any" }, { v: "Remote", label: "Remote" }, { v: "Onsite", label: "Onsite" }, { v: "Relocation", label: "Relocation" }].map(
              (m) => (
                <button
                  key={m.label}
                  type="button"
                  className={`${classes.segBtn} ${heroMode === m.v ? classes.segBtnActive : ""}`}
                  onClick={() => setHeroMode(m.v)}
                >
                  {m.label}
                </button>
              ),
            )}
          </div>
          <button type="button" className={classes.heroSearchBtn} onClick={handleSearch}>
            <IconSearch size={16} /> Search
          </button>
        </div>
      </div>

      <div className={classes.quickRow}>
        <div className={classes.quickChips}>
          <button
            type="button"
            className={`${classes.chip} ${quickChips.fulltime ? classes.chipActive : ""}`}
            onClick={() => toggleQuickChip("fulltime")}
          >
            <IconBriefcase2 size={15} /> Full-Time
          </button>
          <button
            type="button"
            className={`${classes.chip} ${quickChips.remote ? classes.chipActive : ""}`}
            onClick={() => toggleQuickChip("remote")}
          >
            <IconMapPin size={15} /> Remote
          </button>
          <button
            type="button"
            className={`${classes.chip} ${quickChips.week ? classes.chipActive : ""}`}
            onClick={() => toggleQuickChip("week")}
          >
            <IconCalendar size={15} /> Posted this week
          </button>
          <button
            type="button"
            className={`${classes.chip} ${quickChips.relocation ? classes.chipActive : ""}`}
            onClick={() => toggleQuickChip("relocation")}
          >
            <IconPlane size={15} /> Relocation provided
          </button>
        </div>
        <button type="button" className={classes.btnAdvanced} onClick={() => setDrawerOpen(true)}>
          <IconAdjustmentsHorizontal size={16} /> All Filters
        </button>
      </div>

      {!searched ? (
        <div className={classes.presearch}>
          <IconSearch size={34} />
          <div className={classes.presearchTitle}>Search to see matching jobs</div>
          <div className={classes.presearchHint}>
            Type a job title, skill, or company above and hit Search — your results will appear here.
          </div>
        </div>
      ) : (
        <div>
          {activeFilters.length > 0 && (
            <div className={classes.activeFilters}>
              <span className={classes.afLabel}>Filters:</span>
              {activeFilters.map((f) => (
                <span key={f.key} className={classes.filterChip}>
                  {f.label}
                  <button onClick={f.clear} aria-label={`Remove ${f.label}`}>
                    <IconX size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className={classes.resultsHeader}>
            <div className={classes.resultsCount}>
              {filteredJobs.length} job{filteredJobs.length === 1 ? "" : "s"} match your search
            </div>
            <div className={classes.resultsActions}>
              <button className={classes.btnOutline} onClick={handleSaveSearch} disabled={savingSearch}>
                <IconDeviceFloppy size={14} /> {savingSearch ? "Saving…" : "Save this search"}
              </button>
              <div className={classes.selectWrap}>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="newest">Newest First</option>
                  <option value="salary-desc">Salary: High to Low</option>
                  <option value="salary-asc">Salary: Low to High</option>
                </select>
                <IconChevronDown size={15} />
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.jfList}>
              {loading ? (
                <div className={classes.jfEmpty}>
                  <div className={classes.jfEmptyTitle}>Loading…</div>
                </div>
              ) : pagedJobs.length === 0 ? (
                <div className={classes.jfEmpty}>
                  <IconSearch size={32} />
                  <div className={classes.jfEmptyTitle}>No jobs match these filters</div>
                  <div className={classes.jfEmptyHint}>
                    Try removing a filter, or{" "}
                    <span
                      className={classes.jfClearLink}
                      onClick={() => {
                        setHeroMode("");
                        setQuickChips({ fulltime: false, remote: false, week: false, relocation: false });
                        setDrawerFilters(emptyDrawerFilters);
                      }}
                    >
                      clear all filters
                    </span>
                    .
                  </div>
                </div>
              ) : (
                pagedJobs.map((job) => (
                  <div key={job._id} className={classes.jfRow}>
                    <div className={classes.jfRowMain}>
                      <div className={classes.jfIcon}>
                        <IconBriefcase size={20} />
                      </div>
                      <div className={classes.jfRowContent} onClick={() => setSelectedJob(job)}>
                        <div className={classes.jfTitleRow}>
                          <span className={classes.jfTitle}>{job.title}</span>
                        </div>
                        <div className={classes.jfMeta}>
                          {job.location && (
                            <span>
                              <IconMapPin size={13} /> {job.location}
                            </span>
                          )}
                          {job.type && (
                            <>
                              {job.location && <span className={classes.jfMetaDivider} />}
                              <span>
                                <IconClock size={13} /> {job.type}
                              </span>
                            </>
                          )}
                          {job.workMode && (
                            <>
                              <span className={classes.jfMetaDivider} />
                              <span>
                                <IconWorld size={13} /> {job.workMode}
                              </span>
                            </>
                          )}
                          {job.duration && (
                            <>
                              <span className={classes.jfMetaDivider} />
                              <span>{job.duration}</span>
                            </>
                          )}
                        </div>
                        {job.endDate && (
                          <div className={classes.jfDate}>
                            <IconCalendar size={13} /> Deadline {moment(job.endDate).format("DD-MM-YYYY")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={classes.jfRowSide}>
                      <div className={classes.jfSalary}>&#8358;{currencyFormat(Number(job.budget ?? 0))}</div>
                      <div className={classes.jfSideDivider} />
                      <div className={classes.jfActions}>
                        <button
                          className={`${classes.jfIconBtn} ${job.isStarred ? classes.jfIconBtnActive : ""}`}
                          title={job.isStarred ? "Remove star" : "Star this job"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStar(job);
                          }}
                        >
                          {job.isStarred ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                        </button>
                        <button
                          className={classes.jfIconBtn}
                          title="Share this job"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareJob(job);
                          }}
                        >
                          <IconShare size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {totalPages > 1 && (
            <div className={classes.jfPagination}>
              <button className={classes.jfPageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`${classes.jfPageBtn} ${p === page ? classes.jfPageBtnActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button className={classes.jfPageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                ›
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Filter drawer ── */}
      {drawerOpen && (
        <div className={classes.filterOverlay} onClick={() => setDrawerOpen(false)} />
      )}
      <aside className={`${classes.filterDrawer} ${drawerOpen ? classes.filterDrawerOpen : ""}`} aria-label="Advanced search filters">
        <div className={classes.fdHeader}>
          <div>
            <h2>Advanced Search</h2>
            <p>Filter the job feed to find your match.</p>
          </div>
          <button className={classes.fdClose} onClick={() => setDrawerOpen(false)} aria-label="Close filters">
            <IconX size={18} />
          </button>
        </div>
        <div className={classes.fdBody}>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Date Posted</label>
            <div className={classes.selectWrap}>
              <select
                value={drawerFilters.datePosted}
                onChange={(e) => setDrawerFilters((p) => ({ ...p, datePosted: e.target.value }))}
              >
                {DATE_POSTED_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <IconChevronDown size={15} />
            </div>
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Job Title</label>
            <input
              type="text"
              className={classes.filterInput}
              placeholder="e.g. Frontend Developer"
              value={drawerFilters.title}
              onChange={(e) => setDrawerFilters((p) => ({ ...p, title: e.target.value }))}
            />
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Company</label>
            <input
              type="text"
              className={classes.filterInput}
              placeholder="Company name"
              value={drawerFilters.company}
              onChange={(e) => setDrawerFilters((p) => ({ ...p, company: e.target.value }))}
            />
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Location</label>
            <input
              type="text"
              className={classes.filterInput}
              placeholder="City, Country"
              value={drawerFilters.location}
              onChange={(e) => setDrawerFilters((p) => ({ ...p, location: e.target.value }))}
            />
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Skills</label>
            <div className={classes.multiWrap}>
              <div
                className={`${classes.multiTrigger} ${drawerFilters.skills.length ? classes.multiTriggerHasValue : ""}`}
                tabIndex={0}
                onClick={() => setSkillsPanelOpen((p) => !p)}
              >
                {drawerFilters.skills.length === 0 ? (
                  <span>Select skills</span>
                ) : (
                  drawerFilters.skills.map((s) => (
                    <span
                      key={s}
                      className={classes.multiChip}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {s}
                      <span
                        className={classes.rm}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDrawerSkill(s);
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))
                )}
                <IconChevronDown size={16} className={classes.selChevron} />
              </div>
              {skillsPanelOpen && (
                <div className={classes.multiPanel}>
                  {ALL_SKILLS.slice(0, 60).map((s) => (
                    <div
                      key={s}
                      className={`${classes.multiOption} ${drawerFilters.skills.includes(s) ? classes.multiOptionSelected : ""}`}
                      onClick={() => toggleDrawerSkill(s)}
                    >
                      <span className={classes.optBox}>{drawerFilters.skills.includes(s) && <IconCheck size={12} />}</span>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Salary Range (₦)</label>
            <div className={classes.payRow}>
              <div>
                <label>Min</label>
                <input
                  type="number"
                  className={classes.filterInput}
                  placeholder="0"
                  min="0"
                  value={drawerFilters.min}
                  onChange={(e) => setDrawerFilters((p) => ({ ...p, min: e.target.value }))}
                />
              </div>
              <div>
                <label>Max</label>
                <input
                  type="number"
                  className={classes.filterInput}
                  placeholder="Any"
                  min="0"
                  value={drawerFilters.max}
                  onChange={(e) => setDrawerFilters((p) => ({ ...p, max: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Employment Type</label>
            <div className={classes.checkStack}>
              {EMPLOYMENT_TYPES.map((t) => (
                <label
                  key={t}
                  className={`${classes.checkRow} ${drawerFilters.types.includes(t) ? classes.checkRowChecked : ""}`}
                  onClick={() => toggleDrawerType(t)}
                >
                  <span className={classes.checkBox}>{drawerFilters.types.includes(t) && <IconCheck size={13} />}</span> {t}
                </label>
              ))}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Work Mode</label>
            <div className={classes.checkStack}>
              {WORK_MODES.map((m) => (
                <label
                  key={m}
                  className={`${classes.checkRow} ${drawerFilters.modes.includes(m) ? classes.checkRowChecked : ""}`}
                  onClick={() => toggleDrawerMode(m)}
                >
                  <span className={classes.checkBox}>{drawerFilters.modes.includes(m) && <IconCheck size={13} />}</span> {m}
                </label>
              ))}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Relocation</label>
            <div className={classes.checkStack}>
              <label
                className={`${classes.checkRow} ${drawerFilters.relocationOnly ? classes.checkRowChecked : ""}`}
                onClick={() => setDrawerFilters((p) => ({ ...p, relocationOnly: !p.relocationOnly }))}
              >
                <span className={classes.checkBox}>{drawerFilters.relocationOnly && <IconCheck size={13} />}</span> Relocation
                provided only
              </label>
            </div>
          </div>
        </div>
        <div className={classes.fdFooter}>
          <button className={classes.btnOutline} onClick={resetDrawer}>
            <IconRotate size={15} /> Reset
          </button>
          <button className={classes.btnUpdate} onClick={applyDrawer}>
            <IconFilter size={15} /> Show results
          </button>
        </div>
      </aside>

      {/* ── Job detail modal ── */}
      {selectedJob && (
        <div className={classes.modalBackdrop} onClick={() => setSelectedJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={classes.modalHeadIcon}>
                <IconBriefcase size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>{selectedJob.title}</div>
                <div className={classes.modalHeadSub}>
                  {selectedJob.workMode} <span>·</span> Posted {moment(selectedJob.createdAt || selectedJob.startDate).fromNow()}
                </div>
              </div>
              <button className={classes.modalClose} onClick={() => setSelectedJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.detailStats}>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>{selectedJob.applicantTalentIds?.length ?? 0}</div>
                  <div className={classes.detailStatLbl}>Applicants</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>{selectedJob.duration || "—"}</div>
                  <div className={classes.detailStatLbl}>Duration</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>
                    {selectedJob.endDate ? moment(selectedJob.endDate).format("DD MMM") : "—"}
                  </div>
                  <div className={classes.detailStatLbl}>Deadline</div>
                </div>
              </div>

              <div className={classes.detailGrid}>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Salary</div>
                  <div className={classes.detailCellVal}>&#8358;{currencyFormat(Number(selectedJob.budget ?? 0))}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Job Type</div>
                  <div className={classes.detailCellVal}>{selectedJob.type || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Work Mode</div>
                  <div className={classes.detailCellVal}>{selectedJob.workMode || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Location</div>
                  <div className={classes.detailCellVal}>{selectedJob.location || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Relocation</div>
                  <div className={classes.detailCellVal}>
                    {selectedJob.hasNoRelocationPlans === "Yes" ? "Not provided" : "Provided"}
                  </div>
                </div>
              </div>

              {selectedJob.description && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>About this role</div>
                  <div className={classes.detailDesc}>{selectedJob.description}</div>
                </div>
              )}

              {selectedJob.acceptanceCriteria && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Requirements</div>
                  <ul className={classes.detailReqList}>
                    {selectedJob.acceptanceCriteria
                      .split("\n")
                      .filter(Boolean)
                      .map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                  </ul>
                </div>
              )}

              {selectedJob.skillsAndRequirements?.length > 0 && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Skills needed</div>
                  <div className={classes.detailSkills}>
                    {selectedJob.skillsAndRequirements.map((s, i) => (
                      <span key={i} className={classes.detailSkill}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.modalFoot}>
              <button className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={() => setSelectedJob(null)}>
                <IconX size={14} /> Close
              </button>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnStar}`}
                onClick={() => handleToggleStar(selectedJob)}
              >
                {selectedJob.isStarred ? <IconStarFilled size={14} /> : <IconStar size={14} />}{" "}
                {selectedJob.isStarred ? "Saved" : "Save"}
              </button>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnPrimary}`}
                onClick={() => navigate(`/job/${selectedJob._id}`)}
              >
                <IconSend size={14} /> View &amp; Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share modal ── */}
      {shareJob && (
        <div className={classes.modalBackdrop} onClick={() => setShareJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconBlue}`}>
                <IconShare3 size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Share this job</div>
                <div className={classes.modalHeadSub}>{shareJob.title}</div>
              </div>
              <button className={classes.modalClose} onClick={() => setShareJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.shareSocialGrid}>
                {[
                  {
                    Icon: IconBrandTwitter,
                    bg: "#1d9bf0",
                    label: "Twitter",
                    onClick: () =>
                      window.open(
                        `https://twitter.com/intent/tweet?url=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}&text=${encodeURIComponent("Check out this job!")}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandFacebook,
                    bg: "#1877f2",
                    label: "Facebook",
                    onClick: () =>
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandWhatsapp,
                    bg: "#25d366",
                    label: "WhatsApp",
                    onClick: () =>
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(`Check out this job: ${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                ].map(({ Icon, bg, label, onClick }) => (
                  <button key={label} className={classes.shareSocialBtn} onClick={onClick}>
                    <span className={classes.shareSocialIcon} style={{ background: bg }}>
                      <Icon size={19} />
                    </span>
                    {label}
                  </button>
                ))}
              </div>
              <span className={classes.shareCopyLabel}>Or copy link</span>
              <div className={classes.shareCopyRow}>
                <input type="text" readOnly value={`${window.location.origin}/job/${shareJob._id}`} />
                <button
                  className={`${classes.modalBtn} ${classes.modalBtnPrimary} ${classes.shareCopyBtn}`}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/job/${shareJob._id}`);
                      toastSuccess("Job link copied to clipboard!");
                      setShareJob(null);
                    } catch {
                      errorHandler("Failed to copy link", "error");
                    }
                  }}
                >
                  <IconCopy size={14} /> Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`${classes.toast} ${classes.toastShow}`}>
          <IconCircleCheck size={16} /> {toast}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchPage;
